import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Invoice } from '../types/invoice.ts';
import JobInvoice from './JobInvoice.tsx';
import { ArrowLeft, Edit, Download } from 'lucide-react';

const InvoiceView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadInvoice = async () => {
      if (!id || !currentUser) return;

      try {
        setLoading(true);
        setError(null);

        const invoiceDoc = await getDoc(doc(db, 'invoices', id));
        
        if (!invoiceDoc.exists()) {
          setError('Invoice not found');
          return;
        }

        const data = invoiceDoc.data();
        const invoiceData: Invoice = {
          id: invoiceDoc.id,
          ...data,
          invoiceDate: data.invoiceDate?.toDate() || new Date(),
          dueDate: data.dueDate?.toDate() || new Date(),
          periodStart: data.periodStart?.toDate(),
          periodEnd: data.periodEnd?.toDate(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Invoice;

        setInvoice(invoiceData);
      } catch (err) {
        console.error('Error loading invoice:', err);
        setError('Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };

    loadInvoice();
  }, [id, currentUser]);

  // Convert Invoice data to JobInvoice format
  const convertToJobData = (invoice: Invoice) => {
    const employees: any = {};
    
    // Check if this invoice uses activities or direct hours
    const hasActivityDescriptions = invoice.lineItems.some(item => 
      !item.isExpense && item.activityDescription && item.activityDescription.trim() !== ''
    );
    
    // Group line items by employee
    invoice.lineItems.forEach(item => {
      if (!employees[item.employeeName]) {
        employees[item.employeeName] = {
          activities: {},
          expenses: [],
          regularHours: 0,
          overtimeHours: 0,
          regularRate: 0,
          overtimeRate: 0,
          regularTotal: 0,
          overtimeTotal: 0
        };
      }

      if (item.isExpense) {
        // Handle expenses
        employees[item.employeeName].expenses.push({
          description: item.expenseDescription || '',
          amount: item.expenseAmount || 0
        });
      } else if (hasActivityDescriptions && item.activityDescription) {
        // Handle work activities only if activities are actually used
        const activityKey = item.activityDescription;
        if (!employees[item.employeeName].activities[activityKey]) {
          employees[item.employeeName].activities[activityKey] = {
            regularHours: 0,
            overtimeHours: 0,
            regularRate: item.regularRate,
            overtimeRate: item.overtimeRate,
            regularTotal: 0,
            overtimeTotal: 0
          };
        }

        const activity = employees[item.employeeName].activities[activityKey];
        activity.regularHours += item.regularHours;
        activity.overtimeHours += item.overtimeHours;
        activity.regularTotal += item.regularTotal;
        activity.overtimeTotal += item.overtimeTotal;
      } else {
        // Handle direct hours (no activities) - for BTC/REL invoices
        employees[item.employeeName].regularHours += item.regularHours;
        employees[item.employeeName].overtimeHours += item.overtimeHours;
        employees[item.employeeName].regularRate = item.regularRate;
        employees[item.employeeName].overtimeRate = item.overtimeRate;
        employees[item.employeeName].regularTotal += item.regularTotal;
        employees[item.employeeName].overtimeTotal += item.overtimeTotal;
      }
    });

    return {
      jobName: invoice.jobName,
      jobNumber: invoice.jobNumber,
      weekEnding: invoice.weekEnding,
      clientName: invoice.clientName,
      clientAddress: invoice.clientAddress,
      clientCity: invoice.clientCity,
      clientState: invoice.clientState,
      clientZip: invoice.clientZip,
      employees
    };
  };

  const businessInfo = {
    name: "7Twenty4 Services LLC",
    address: "200 Greenlea St",
    city: "Pulaski",
    state: "VA",
    zip: "24301",
    phone: "(972) 333-0913",
    email: "kim@7twenty4services.com",
    paymentTerms: "Net 30"
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{error || 'Invoice not found'}</p>
          <button
            onClick={() => navigate('/invoices')}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Invoices
          </button>
        </div>
      </div>
    );
  }

  const jobData = convertToJobData(invoice);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/invoices')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Invoices
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invoice {invoice.invoiceNumber}</h1>
            <p className="text-gray-600">View invoice details</p>
          </div>
        </div>
        <div className="flex gap-3">
          {(currentUser?.permissions?.includes('admin') || currentUser?.permissions?.includes('edit_invoices')) && (
            <button
              onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Invoice
            </button>
          )}
        </div>
      </div>

      {/* Invoice Display */}
      <JobInvoice
        jobData={jobData}
        invoiceNumber={invoice.invoiceNumber}
        useBtcPrefix={invoice.useBtcPrefix}
        useRelPrefix={invoice.useRelPrefix}
        businessInfo={businessInfo}
        onSaveSuccess={() => {
          // Invoice is already saved, just show success message
          console.log('Invoice viewed successfully');
        }}
      />
    </div>
  );
};

export default InvoiceView;
